import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ModalController } from '@ionic/angular';
import { MatDialog } from '@angular/material/dialog';
import { ISortState } from './table-dados-bancarios.interface';
import { ToastService } from 'src/app/modules/services/toast/toast.service';
import { ModalComponent } from '../../modal/modal.component';
import { ModalDadosBancariosComponent } from '../modal-dados-bancarios/modal-dados-bancarios.component';
import { ModalHistoricComponent } from 'src/app/modules/shared/modals/modal-historic/modal-historic.component';

@Component({
  selector: 'app-table-dados-bancarios',
  templateUrl: './table-dados-bancarios.component.html',
  styleUrls: ['./table-dados-bancarios.component.scss'],
})
export class TableDadosBancariosComponent {
  @Input() allData: any[] = [];
  @Input() isMaster: boolean = false;
  @Output() allDataChange = new EventEmitter<any>();
  @Input() inclusaoDisabled: boolean = false;
  @Input() exclusaoDisabled: boolean = false;
  @Input() edicaoDisabled: boolean = false;
  disabledTooltipMessage: string = 'Ação não permitida';

  sortState: ISortState = { sortProperty: 'banco', sortDirection: 'asc' };
  pageSize: number = 10;
  pageIndex: number = 0;
  totalPages: number = 1;
  totalElements: number = 0;
  displayedColumns: string[] = ['banco', 'agencia', 'conta', 'actions'];

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  constructor(
    private toastService: ToastService,
    private modalCtrl: ModalController,
    public dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['allData']) {
      this.allData = this.allData;
      this.dataSource.data = this.allData;
      this.setPaginatorValues();
    }
  }
  setPaginatorValues() {
    this.totalElements = this.dataSource.data.length;
    this.totalPages = Number(
      (1 + this.totalElements / this.pageSize).toPrecision(1)
    );
  }
  async openNewDadoBancarioDialog(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ModalDadosBancariosComponent,
      backdropDismiss: false,
      cssClass: 'modal-lg',
      componentProps: { isEditMode: false },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.allData.push(data);
      this.dataSource.data = this.allData;
      this.allDataChange.emit(this.allData);
    }
  }

  async openEditDadoBancarioDialog(dadoBancario: any): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ModalDadosBancariosComponent,
      backdropDismiss: false,
      cssClass: 'modal-lg',
      componentProps: { data: dadoBancario, isEditMode: true },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.onUpdate(data);
    }
  }

  openDeleteDialog(id: string): void {
    const modalTitle: string = 'Excluir';
    const modalContent: string =
      'Você tem certeza que deseja excluir este dado bancário?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onDelete.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  openInactivateDialog(id: any): void {
    const modalTitle: string = 'Inativar';
    const modalContent: string =
      'Você tem certeza que deseja inativar este dado bancário?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onInactivateModalConfirm.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  openActivateDialog(id: any): void {
    const modalTitle: string = 'Ativar';
    const modalContent: string =
      'Você tem certeza que deseja ativar este dado bancário?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onActivateModalConfirm.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  onInactivateModalConfirm(id: string): void {
    let dadoBancarioObj = this.allData.find((data) =>
      data.id ? data.id === id : data.tempId === id
    );
    dadoBancarioObj.status = false;

    this.onUpdate(dadoBancarioObj);
    this.toastService.showSuccess('Dado bancário ativado com sucesso');
  }

  onActivateModalConfirm(id: string): void {
    let dadoBancarioObj = this.allData.find((data) =>
      data.id ? data.id === id : data.tempId === id
    );
    dadoBancarioObj.status = true;

    this.onUpdate(dadoBancarioObj);
    this.toastService.showSuccess('Dado bancário ativado com sucesso');
  }

  onDelete(id: string) {
    let newDataArray = this.allData.filter((oldData) =>
      oldData.id ? oldData.id !== id : oldData.tempId !== id
    );
    this.allData = newDataArray;
    this.dataSource.data = newDataArray;
    this.toastService.showSuccess('Dado bancário removido com sucesso');
    this.allDataChange.emit(this.allData);
  }

  onUpdate(data: any) {
    let newDataArray = this.allData.filter((oldData) =>
      oldData.id ? oldData.id !== data.id : oldData.tempId !== data.tempId
    );
    newDataArray.push(data);
    this.allData = newDataArray;
    this.dataSource.data = newDataArray;
    this.allDataChange.emit(this.allData);
  }

  onSortChange(sortProperty: ISortState['sortProperty']) {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection =
        this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }

    const sortedData = this.dataSource.data.sort((a, b) => {
      let comparison = 0;
      if (a[sortProperty] > b[sortProperty]) {
        comparison = 1;
      } else if (a[sortProperty] < b[sortProperty]) {
        comparison = -1;
      }

      return this.sortState.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.dataSource.data = sortedData;
  }

  onSortChangeKeydown(
    event: KeyboardEvent,
    sortProperty: ISortState['sortProperty']
  ) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      this.onSortChange(sortProperty);
    }
  }
  async openDialogHistoricoDadosBancarios(dataId:any){
    const data = {
        type: 'dadosBancarios',
        id: dataId,
        fieldMappings: {
          banco: 'Banco',
          agencia: 'Agência',
          conta: 'Conta',
        }
      }
      const modal = await this.modalCtrl.create({
        component: ModalHistoricComponent,
        componentProps: {
          data: data,
          fieldMappings: data.fieldMappings,
        },
      });
  
      modal.present();
    }
}
